import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { Source } from '../../../Common/UsageDataProvider';
import { useAppSelector } from 'flex/utils';

const PAGE_SIZE = 50;
const FIRST_PAGE = 1;

type FetchAction = (
    params: DefaultFetchParams,
    page?: number,
    config?: Record<string, number | string | boolean>
) => AnyAction;

type DefaultFetchParams = {
    page_size: number;
    [key: string]: string | number | undefined;
};

interface Params {
    pageSize?: number;
    /**
     * 変換された後のデータ。
     */
    data: Source['organizationAppLists'][string];
    /**
     * 別のクエリ文字列。
     * 例:GET /users?name=john, query = [{ name: "john" }]
     */
    query?: [{ [key: string]: string }];
    /**
     * APIからデータをフィルタリングするために使用されるクエリ文字列パラメータのキー。
     * 例:GET /users?name=john, queryKey = "name"
     */
    queryKey?: string;
    selector: (state: Store) => Store['appsListConnection'];
    /**
     * スクロールまたは検索イベント時にディスパッチされるフェッチアクション
     */
    fetchAction: FetchAction;
    /**
     * アンマウント時にディスパッチされるクリーンアップアクション
     */
    cleanupAction?: AnyAction;
}

/**
 * 無限スクロール+サーバー検索用のフック（AutoComplete / Select）。
 * ページネーション、読み込み状態、およびReduxデータ同期を自動的に管理する。
 * 次ページの取得及びキーワードによるデータ検索機能を提供する。
 */
const useSelectScroll = ({
    queryKey,
    query,
    data,
    pageSize = PAGE_SIZE,
    selector,
    fetchAction,
    cleanupAction
}: Params) => {
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);

    const connection = useAppSelector(selector);

    // 次のページがあるかどうかを確認する。
    const hasMore = data.page < data.pageTotal;

    // 次ページ取得処理。検索キーワード・追加クエリ・ページ番号をパラメータに設定してAPIを呼び出す。
    const handleNextPage = (searchText?: string) => {
        if (!isLoading) {
            setIsSearchMode(false);
            setIsScrolling(true);
            // ページサイズを設定
            let params: DefaultFetchParams = { page_size: pageSize };

            // 追加クエリが存在する場合、パラメータにマージ
            if (Array.isArray(query) && query.length > 0) {
                query.forEach((element) => {
                    params = { ...params, ...element };
                });
            }

            // 検索キーワードが存在する場合、クエリキーにセット
            if (typeof queryKey === 'string' && searchText !== '') {
                params[queryKey] = searchText;
            }

            // 次ページ番号を設定
            const nextPage = data.page + 1;

            dispatch(
                fetchAction(params, nextPage, {
                    isScrollEvent: true, // 現在スクロール中であるかどうかを確認する。
                    page: nextPage,
                })
            );
        }
    };

    const handleSearch = (searchText: string) => {
        if (!isLoading && !isSearching) {

            setIsSearching(true);
            setIsSearchMode(true);

            // ページサイズを設定
            let params: DefaultFetchParams = { page_size: pageSize };

            // 追加クエリが存在する場合、パラメータにマージ
            if (Array.isArray(query) && query.length > 0) {
                query.forEach((element) => {
                    params = { ...params, ...element };
                });
            }

            // 検索キーワードが存在する場合、クエリキーにセット
            if (typeof queryKey === 'string' && searchText !== '') {
                params[queryKey] = searchText;
            }

            dispatch(
                fetchAction(params, FIRST_PAGE, {
                    isScrollEvent: false, // 現在スクロール中であるかどうかを確認する。
                    page: FIRST_PAGE,
                })
            );
        }
    };

    useEffect(() => {
        return () => {
            if (cleanupAction) {
                dispatch(cleanupAction);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!connection?.meta.fetch) {
            if (connection?.payload?.result) {
                setIsLoading(false);
            }
            // スクロール完了後、isScrollingをfalseにリセット
            if (connection?.meta.config?.isScrollEvent) {
                setIsScrolling(false);
            }

            if (!connection?.meta.config?.isScrollEvent) {
                setIsSearching(false);
            }
        } else {
            setIsLoading(true);
        }

    }, [connection]);

    return {
        dataOptions: data.list,
        isLoading,
        isSearching,
        isSearchMode,
        isScrolling,
        hasMore,
        handleNextPage,
        handleSearch,
    };
};

export default useSelectScroll;
